import React, { useState, useCallback } from 'react';
import { Input, Button, Tooltip } from 'antd';
import {
    SearchOutlined,
    CloseCircleOutlined,
    MenuOutlined,
    LineOutlined,
    TagsOutlined,
    TagOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const SearchInput = ({
    value,
    onChange,
    onSearch,
    onClear,
    placeholder,
    textareaPlaceholder,
    className,
    style
}) => {
    const [isTextAreaMode, setIsTextAreaMode] = useState(false);

    // Helper function to convert between comma-separated and newline-separated formats
    const convertSearchText = useCallback((text, toNewlines = true) => {
        if (!text) return '';
        
        if (toNewlines) {
            // Convert comma-separated to newline-separated
            return text.split(',').map(item => item.trim()).filter(item => item.length > 0).join('\n');
        } else {
            // Convert newline-separated to comma-separated
            return text.split('\n').map(item => item.trim()).filter(item => item.length > 0).join(',');
        }
    }, []);

    // Get search text in comma-separated format for API calls (does not modify display text)
    const getSearchTextForAPI = useCallback((text) => {
        if (!text) return '';
        
        // Always return comma-separated format regardless of input format
        // This only converts for API, does not change the display text
        if (text.includes('\n')) {
            return convertSearchText(text, false);
        }
        return text;
    }, [convertSearchText]);

    // Handle paste events to detect Google Sheets data
    const handlePaste = useCallback((e) => {
        const pastedText = e.clipboardData.getData('text');
        
        // Check if pasted text contains newlines (likely from Google Sheets column copy)
        if (pastedText && pastedText.includes('\n')) {
            e.preventDefault();
            
            // Process the pasted text - clean up and format
            const cleanedLines = pastedText
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0);
            
            if (cleanedLines.length > 1) {
                // Switch to textarea mode for better UX with multiple lines
                setIsTextAreaMode(true);
               
                // Join with newlines for textarea display
                const formattedText = cleanedLines.join('\n');
                onChange(formattedText);
            } else if (cleanedLines.length === 1) {
                // Single line, keep current mode
                onChange(cleanedLines[0]);
            }
        }
    }, [onChange]);

    // Handle input change
    const handleInputChange = useCallback((e) => {
        const inputValue = e.target.value;
        
        // Auto-detect newlines and switch to textarea mode
        if (!isTextAreaMode && inputValue.includes('\n')) {
            setIsTextAreaMode(true);
        }
        
        onChange(inputValue);
    }, [onChange, isTextAreaMode]);

    // Toggle between textarea and input modes
    const toggleSearchMode = useCallback(() => {
        const currentText = value || '';
        let convertedText = '';
        
        if (isTextAreaMode) {
            // Switching to single line - convert newlines to commas
            convertedText = convertSearchText(currentText, false);
        } else {
            // Switching to textarea - convert commas to newlines
            convertedText = convertSearchText(currentText, true);
        }
        
        setIsTextAreaMode(!isTextAreaMode);
        onChange(convertedText);
    }, [isTextAreaMode, value, convertSearchText, onChange]);

    // Handle search - convert to comma-separated for API but keep display text unchanged
    const handleSearch = useCallback(() => {
        const apiSearchText = getSearchTextForAPI(value);
        onSearch(apiSearchText); // Send comma-separated format to API
        // Note: 'value' (display text) remains unchanged in textarea mode
    }, [value, onSearch, getSearchTextForAPI]);

    // Handle clear
    const handleClear = useCallback(() => {
        setIsTextAreaMode(false);
        onClear();
    }, [onClear]);

    return (
        <div style={{ position: 'relative', ...style }} className={className}>
            {isTextAreaMode ? (
                <TextArea
                    placeholder={textareaPlaceholder || "Search by SKU, ASIN or product name (one per line or use commas)"}
                    value={value}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    style={{ 
                        width: '100%', 
                        backgroundColor: 'transparent',
                        resize: 'vertical',
                        minHeight: 80
                    }}
                    variant='underlined'
                    autoSize={{ minRows: 3, maxRows: 8 }}
                />
            ) : (
                <Input
                    prefix={<SearchOutlined style={{ color: '#999' }} />}
                    placeholder={placeholder || "Search by SKU, ASIN or product name (use commas for multiple SKUs/ASINs)"}
                    value={value}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    onPressEnter={handleSearch}
                    style={{ width: '100%', backgroundColor: 'transparent' }}
                    variant='underlined'
                    size="middle"
                />
            )}
            
            {/* Search button for textarea mode (since Enter doesn't work) */}
            {isTextAreaMode && value && (
                <Button 
                    type="text" 
                    size="small" 
                    icon={<SearchOutlined />} 
                    onClick={handleSearch}
                    style={{ 
                        position: 'absolute', 
                        right: value ? 56 : 32, 
                        top: 8,
                        color: '#999',
                        padding: 0, 
                        minWidth: 'auto',
                        zIndex: 1,
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px'
                    }}
                    title="Search now"
                />
            )}
            
            {/* Toggle button for switching between input and textarea */}
            <Button 
                type="text" 
                size="small" 
                icon={isTextAreaMode ? <TagOutlined /> : <Tooltip title="Switch to multi-line input"><TagsOutlined /></Tooltip>} 
                onClick={toggleSearchMode}
                style={{ 
                    position: 'absolute', 
                    right: value ? (isTextAreaMode ? 32 : 32) : 8, 
                    top: isTextAreaMode ? 8 : '50%', 
                    transform: isTextAreaMode ? 'none' : 'translateY(-50%)',
                    padding: 0,
                    color: '#999',
                    minWidth: 'auto',
                    zIndex: 1,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px'
                }}
                title={isTextAreaMode ? "Switch to single line input" : "Switch to multi-line input"}
            />
            
            {/* Clear button */}
            {value && (
                <Button 
                    type="text" 
                    size="small" 
                    icon={<CloseCircleOutlined />} 
                    onClick={handleClear}
                    style={{ 
                        position: 'absolute', 
                        right: 8, 
                        top: isTextAreaMode ? 8 : '50%', 
                        transform: isTextAreaMode ? 'none' : 'translateY(-50%)',
                        color: '#999', 
                        padding: 0, 
                        minWidth: 'auto',
                        zIndex: 1,
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px'
                    }}
                    title="Clear search and reload"
                />
            )}
        </div>
    );
};

export default SearchInput;